import { useLiveRefresh } from "../utils/live";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Spinner from "../components/common/Spinner";
import Modal from "../components/common/Modal";
import { listQuotes, sendQuoteToCustomer, markQuoteLost, approveQuoteSend, sendBackQuote, openQuotePdf } from "../api/quotes";
import { listLeads, logLeadTouch } from "../api/leads";
import { listQuoteRequests, resolveQuoteRequest } from "../api/quotePage";
import { listEmailDrafts, dismissEmail, createNudge, recentNudges } from "../api/email";
import EmailDraftModal from "../components/email/EmailDraftModal";
import LeadConversation from "../components/leads/LeadConversation";
import { fillName } from "../utils/emailText";
import QuoteBuilder from "../components/admin/QuoteBuilder";
import { formatMoney as formatUSD } from "../utils/money";
import { formatEasternDate } from "../utils/dates";
import { balanceCents, amountPaidCents, payPageUrl } from "../utils/jobs";
import { buildFollowups, LOST_REASONS } from "../utils/followups";
import { telHref, smsHref, firstName, senderName, timeAgo, viewSummary, daysSince } from "../utils/contact";
import { notifyCountsChanged } from "../utils/navCounts";

// The chase list: everything that needs a nudge from you, most urgent money
// first. It empties itself as things get paid, re-sent or written off.

function shortDate(ms) {
  return new Date(ms).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

function quoteNudge(q) {
  const when = q.event_date ? ` for ${formatEasternDate(q.event_date)}` : "";
  const url = payPageUrl(q);
  return (
    `Hi ${firstName(q.customer_name)}, just checking in on your Birdie Bus quote${when}. ` +
    `Happy to answer any questions` +
    (url ? ` — you can lock in the date here: ${url}` : ".")
  );
}

function balanceNudge(q) {
  const url = payPageUrl(q);
  return (
    `Hi ${firstName(q.customer_name)}, quick reminder that the remaining ` +
    `${formatUSD(balanceCents(q))} for your Birdie Bus event is due by ${shortDate(q._dueMs)}.` +
    (url ? ` You can pay here: ${url}` : "")
  );
}

export default function AdminFollowups() {
  const [quotes, setQuotes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [losing, setLosing] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reviewing, setReviewing] = useState(null); // a customer request open in the builder
  const [emails, setEmails] = useState([]);
  const [emailsError, setEmailsError] = useState("");
  const [nudges, setNudges] = useState([]);
  const [openEmail, setOpenEmail] = useState(null); // {id, note}
  const [talking, setTalking] = useState(null); // lead id with its conversation open
  const [openQuote, setOpenQuote] = useState(null); // a quote waiting for approval, open in the builder
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError("");
    try {
      const [q, l, r, e, n] = await Promise.all([
        listQuotes({}),
        listLeads().catch(() => []),
        listQuoteRequests().catch(() => []),
        listEmailDrafts().catch((err) => {
          setEmailsError(err.message || "Couldn't load emails waiting for approval.");
          return null;
        }),
        recentNudges().catch(() => []),
      ]);
      setQuotes(q);
      setLeads(l);
      setRequests(r);
      if (e) {
        setEmails(e);
        setEmailsError("");
      }
      setNudges(n);
    } catch (e) {
      setError(e.message || "Could not load follow-ups.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(load);

  const f = useMemo(() => buildFollowups(quotes, leads), [quotes, leads]);
  // A quiet lead's follow-ups wait here like every other email, with their own buttons.
  const followups = useMemo(() => emails.filter((d) => d.kind === "campaign"), [emails]);
  const otherEmails = useMemo(() => emails.filter((d) => d.kind !== "campaign"), [emails]);
  // Drafts an API key (an AI assistant, another app) asked to send. Nothing goes until you approve.
  const approvals = useMemo(
    () =>
      quotes
        .filter((q) => q.status === "draft" && q.send_requested_at)
        .sort((a, b) => new Date(a.send_requested_at) - new Date(b.send_requested_at)),
    [quotes]
  );

  async function approve(q) {
    if (!q.customer_email) {
      setError(`${q.customer_name} has no email on file, so there's nothing to send it to.`);
      return;
    }
    if (!window.confirm(`Email ${q.customer_name} (${q.customer_email}) quote ${q.quote_number} for ${formatUSD(q.total_cents)}, with the payment link?`))
      return;
    setBusyId(q.id);
    setError("");
    try {
      await approveQuoteSend(q.id, q.updated_at);
      setNotice(`Sent ${q.quote_number} to ${q.customer_email}.`);
    } catch (e) {
      setError(e.message || "Couldn't send it.");
    } finally {
      setBusyId(null);
      await load();
      notifyCountsChanged();
    }
  }

  async function sendBack(q) {
    const reason = window.prompt(`What should change on ${q.quote_number}? ${q.api_key_name || "The app"} sees your note.`, "");
    if (reason === null) return;
    setBusyId(q.id);
    try {
      await sendBackQuote(q.id, reason);
      setNotice(`${q.quote_number} sent back. It stays a draft until it's asked for again.`);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Couldn't send it back.");
    } finally {
      setBusyId(null);
    }
  }

  async function pdf(q) {
    try {
      await openQuotePdf(q.id);
    } catch (e) {
      setError(e.message || "Couldn't open the PDF.");
    }
  }

  async function resend(q) {
    if (!q.customer_email) {
      setError(`${q.customer_name} has no email on file — text them instead.`);
      return;
    }
    if (!window.confirm(`Email ${q.customer_name} the quote and payment link again?`)) return;
    setBusyId(q.id);
    setError("");
    try {
      await sendQuoteToCustomer(q.id);
      setNotice(`Re-sent to ${q.customer_email}. It'll come back here in a few days if still unpaid.`);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not re-send the quote.");
    } finally {
      setBusyId(null);
    }
  }

  async function copyLink(q) {
    const url = payPageUrl(q);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setNotice("Pay link copied.");
    } catch {
      setError("Couldn't copy — open the job to grab the link.");
    }
  }

  async function lose(q, reason) {
    setBusyId(q.id);
    try {
      await markQuoteLost(q.id, reason);
      setLosing(null);
      setNotice(`${q.quote_number} marked lost — calendar hold and payment link removed.`);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not mark the quote lost.");
    } finally {
      setBusyId(null);
    }
  }

  const openJob = (q) => navigate(`/admin/jobs?open=${q.id}`);

  async function dismissRequest(req) {
    if (!window.confirm(`Dismiss ${req.customer_name}'s request? Nothing is sent to them.`)) return;
    try {
      await resolveQuoteRequest(req.id, "dismissed");
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not dismiss the request.");
    }
  }

  // Closing the builder after saving counts as handling the request.
  async function finishReview(changed) {
    const req = reviewing;
    setReviewing(null);
    if (!changed || !req) return;
    try {
      await resolveQuoteRequest(req.id, "done");
      setNotice(`${req.quote_number} updated. If you used Save & send, ${firstName(req.customer_name)} has the new quote.`);
    } catch {
      /* the request just stays listed */
    }
    await load();
    notifyCountsChanged();
  }

  async function skipEmail(d) {
    if (!window.confirm(`Dismiss “${d.subject || d.kind_label}”? It won't be sent or drafted again.`)) return;
    try {
      await dismissEmail(d.id);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not dismiss the email.");
    }
  }

  // A ready-written email for one row, opened for review (nothing sends until you tap Send).
  async function nudge(kind, ids) {
    setError("");
    if (ids.lead_id) setBusyId(`lead-${ids.lead_id}`);
    try {
      const d = await createNudge({ kind, ...ids });
      setOpenEmail({ id: d.id, note: d.ai_note || "" });
    } catch (e) {
      setError(e.message || "Couldn't write that email.");
    } finally {
      setBusyId(null);
    }
  }

  async function campaignAct(d, action) {
    const who = firstName(d.lead_name) || "them";
    if (action === "skip" && !window.confirm(`Skip this follow-up to ${who}? The next one is drafted on schedule.`)) return;
    setBusyId(`email-${d.id}`);
    setError("");
    try {
      if (action === "replied") await logLeadTouch(d.lead_id, { kind: "replied" });
      else await dismissEmail(d.id);
      setNotice(
        action === "replied"
          ? `Marked ${who} as replied. This follow-up is withdrawn and no more are drafted.`
          : `Skipped. The next follow-up to ${who} is drafted on schedule.`
      );
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "That didn't work.");
    } finally {
      setBusyId(null);
    }
  }

  const emailedAt = ({ quote_id, lead_id }) =>
    nudges
      .filter((n) => (quote_id && n.quote_id === quote_id) || (lead_id && n.lead_id === lead_id))
      .map((n) => n.sent_at)
      .sort()
      .pop();

  const total = f.count + requests.length + emails.length + approvals.length;

  return (
    <AppShell title="Follow-ups">
      {error && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}
      {emailsError && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Emails waiting for approval couldn't load: {emailsError}
        </div>
      )}
      {notice && !error && (
        <div className="mb-3 rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <Spinner label="Loading..." />
        </div>
      ) : total === 0 ? (
        <div className="bg-white border border-birdie-green/15 rounded-2xl p-10 text-center">
          <div className="text-4xl mb-2">✅</div>
          <h2 className="font-accent text-lg text-birdie-charcoal">Nothing to chase</h2>
          <p className="text-sm text-birdie-charcoal/60 mt-1">
            Every lead has a quote, every quote is fresh, and no balances are coming due.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <Section title="Quotes to approve" tone="green" items={approvals}>
            {(q) => (
              <Row
                key={`approve-${q.id}`}
                title={q.customer_name}
                customerId={q.customer_id}
                sub={
                  <>
                    <strong className="text-birdie-charcoal/80">
                      {q.quote_number} · {formatUSD(q.total_cents)}
                    </strong>
                    {[q.title, q.event_date && formatEasternDate(q.event_date)].filter(Boolean).map((x) => ` · ${x}`).join("")}
                    <span className="block mt-0.5">
                      Built by {q.api_key_name || "an API key"} · to {q.customer_email || "no email on file"}
                    </span>
                    {q.send_request_note && <span className="block mt-0.5 italic">"{q.send_request_note}"</span>}
                  </>
                }
                meta={timeAgo(q.send_requested_at)}
              >
                <Act primary onClick={() => approve(q)} disabled={busyId === q.id}>
                  {busyId === q.id ? "Sending…" : "Approve & send"}
                </Act>
                <Act onClick={() => pdf(q)}>PDF</Act>
                <Act onClick={() => setOpenQuote(q.id)}>Review</Act>
                <Act onClick={() => sendBack(q)} disabled={busyId === q.id}>
                  Send back
                </Act>
              </Row>
            )}
          </Section>

          <Section title="Emails to approve" tone="green" items={otherEmails}>
            {(d) => (
              <Row
                key={`email-${d.id}`}
                title={d.kind_label}
                sub={
                  <>
                    <strong className="text-birdie-charcoal/80">{fillName(d.subject, "") || "(no subject yet)"}</strong>
                    <span className="block mt-0.5">
                      {d.status === "sending"
                        ? `Sending… ${d.sent} of ${d.recipient_count}`
                        : `To ${d.recipients} ${d.recipients === 1 ? "person" : "people"}`}
                      {[d.customer_name, d.event_date && formatEasternDate(d.event_date)]
                        .filter(Boolean)
                        .map((x) => ` · ${x}`)
                        .join("")}
                    </span>
                  </>
                }
                meta={timeAgo(d.created_at)}
              >
                <Act primary onClick={() => setOpenEmail({ id: d.id })}>
                  {d.status === "sending" ? "Open" : "Review & send"}
                </Act>
                {d.status === "draft" && <Act onClick={() => skipEmail(d)}>Dismiss</Act>}
              </Row>
            )}
          </Section>

          <Section title="Follow-ups to approve" tone="green" items={followups}>
            {(d) => (
              <Row
                key={`fu-${d.id}`}
                title={d.lead_name || "A lead"}
                sub={
                  <>
                    <strong className="text-birdie-charcoal/80">{fillName(d.subject, firstName(d.lead_name))}</strong>
                    <span className="block mt-0.5">
                      No-reply follow-up {(d.audience?.step ?? 0) + 1} of {d.campaign_total}. If they've replied,
                      mark it and this one is withdrawn.
                    </span>
                  </>
                }
                meta={timeAgo(d.created_at)}
              >
                <Act primary onClick={() => setOpenEmail({ id: d.id })}>
                  Review &amp; send
                </Act>
                <Act onClick={() => campaignAct(d, "replied")} disabled={busyId === `email-${d.id}`}>
                  They replied
                </Act>
                <Act onClick={() => campaignAct(d, "skip")} disabled={busyId === `email-${d.id}`}>
                  Skip
                </Act>
                <Act onClick={() => setTalking(d.lead_id)}>Conversation</Act>
              </Row>
            )}
          </Section>

          <Section title="Customer requests" tone="green" items={requests}>
            {(r) => (
              <Row
                key={`req-${r.id}`}
                title={r.customer_name}
                customerId={r.customer_id}
                sub={
                  <>
                    Wants: <strong>{r.items.map((i) => i.name).join(", ")}</strong>
                    <span className="block mt-0.5">
                      About +{formatUSD(r.est_cents)} before tax · {r.quote_number}
                    </span>
                    {r.note && <span className="block mt-0.5 italic">"{r.note}"</span>}
                  </>
                }
                meta={timeAgo(r.created_at)}
              >
                {telHref(r.customer_phone) && <LinkBtn href={telHref(r.customer_phone)}>Call</LinkBtn>}
                <Act primary onClick={() => setReviewing(r)}>
                  Review &amp; send
                </Act>
                <Act onClick={() => dismissRequest(r)}>Dismiss</Act>
              </Row>
            )}
          </Section>

          <Section title="Reply to new leads" tone="mustard" items={f.newLeads}>
            {(l) => (
              <Row
                key={`lead-${l.id}`}
                title={l.name || l.email || l.phone || "Unnamed lead"}
                sub={[l.event_date_text && `📅 ${l.event_date_text}`, l.event_type].filter(Boolean).join(" · ")}
                meta={
                  l.first_reply_at
                    ? `Replied ${timeAgo(l.replied_at)}`
                    : `${l.source === "website" ? "Website" : "Logged"} · ${timeAgo(l.created_at)}`
                }
                emailed={emailedAt({ lead_id: l.id })}
              >
                {l.email && (
                  <Act onClick={() => nudge("nudge_lead", { lead_id: l.id })} disabled={busyId === `lead-${l.id}`}>
                    {busyId === `lead-${l.id}` ? "Writing…" : "Email"}
                  </Act>
                )}
                <Act onClick={() => setTalking(l.id)}>Conversation</Act>
                {telHref(l.phone) && <LinkBtn href={telHref(l.phone)}>Call</LinkBtn>}
                {l.phone && (
                  <LinkBtn
                    href={smsHref(
                      l.phone,
                      `Hi ${firstName(l.name)}, it's ${senderName() || "the team"} from Birdie Bus — thanks for reaching out!`
                    )}
                  >
                    Text
                  </LinkBtn>
                )}
                <Act primary onClick={() => navigate("/admin")}>
                  Quote in Inbox
                </Act>
              </Row>
            )}
          </Section>

          <Section title="Balances coming due" tone="blue" items={f.balances}>
            {(q) => (
              <Row
                key={`bal-${q.id}`}
                title={q.customer_name}
                customerId={q.customer_id}
                sub={`${formatUSD(balanceCents(q))} still owed · ${formatUSD(amountPaidCents(q))} paid`}
                emailed={emailedAt({ quote_id: q.id })}
                meta={
                  q._overdue ? (
                    <span className="text-red-600 font-semibold">Overdue — was due {shortDate(q._dueMs)}</span>
                  ) : (
                    `Due ${shortDate(q._dueMs)}`
                  )
                }
              >
                {telHref(q.customer_phone) && <LinkBtn href={telHref(q.customer_phone)}>Call</LinkBtn>}
                {q.customer_phone && <LinkBtn href={smsHref(q.customer_phone, balanceNudge(q))}>Text reminder</LinkBtn>}
                {q.customer_email && <Act onClick={() => nudge("nudge_balance", { quote_id: q.id })}>Email reminder</Act>}
                <Act onClick={() => copyLink(q)}>Copy pay link</Act>
                <Act onClick={() => openJob(q)}>Open</Act>
              </Row>
            )}
          </Section>

          <Section title="Quotes expiring" tone="red" items={f.expiring}>
            {(q) => (
              <QuoteRow
                key={`exp-${q.id}`}
                q={q}
                meta={
                  q._expired ? (
                    <span className="text-red-600 font-semibold">Expired {shortDate(q._untilMs)}</span>
                  ) : (
                    `Expires ${shortDate(q._untilMs)}`
                  )
                }
                busy={busyId === q.id}
                onResend={() => resend(q)}
                onCopy={() => copyLink(q)}
                onLost={() => setLosing(q)}
                onOpen={() => openJob(q)}
                onEmail={q.customer_email ? () => nudge("nudge_expiring", { quote_id: q.id }) : null}
                emailed={emailedAt({ quote_id: q.id })}
              />
            )}
          </Section>

          <Section title="No reply yet" tone="mustard" items={f.stale}>
            {(q) => (
              <QuoteRow
                key={`stale-${q.id}`}
                q={q}
                meta={
                  q._eventPassed ? (
                    <span className="text-red-600 font-semibold">Event date has passed</span>
                  ) : (
                    `Sent ${q._sentAge} day${q._sentAge === 1 ? "" : "s"} ago`
                  )
                }
                busy={busyId === q.id}
                onResend={() => resend(q)}
                onCopy={() => copyLink(q)}
                onLost={() => setLosing(q)}
                onOpen={() => openJob(q)}
                onEmail={q.customer_email && !q._eventPassed ? () => nudge("nudge_quote", { quote_id: q.id }) : null}
                emailed={emailedAt({ quote_id: q.id })}
              />
            )}
          </Section>

          <Section title="Built but never sent" tone="plain" items={f.drafts}>
            {(q) => (
              <Row
                key={`draft-${q.id}`}
                title={q.customer_name}
                customerId={q.customer_id}
                sub={`${q.title || "Untitled"} · ${formatUSD(q.total_cents)}`}
                meta={`Built ${q._age} day${q._age === 1 ? "" : "s"} ago`}
              >
                <Act onClick={() => setLosing(q)} disabled={busyId === q.id}>
                  Lost
                </Act>
                <Act primary onClick={() => openJob(q)}>
                  Finish &amp; send
                </Act>
              </Row>
            )}
          </Section>
        </div>
      )}

      {openEmail && (
        <EmailDraftModal
          draftId={openEmail.id}
          note={openEmail.note}
          onClose={(changed) => {
            setOpenEmail(null);
            if (changed) load().then(notifyCountsChanged);
          }}
        />
      )}

      {talking && (
        <LeadConversation
          leadId={talking}
          onClose={(changed) => {
            setTalking(null);
            if (changed) load().then(notifyCountsChanged);
          }}
        />
      )}

      {openQuote && (
        <QuoteBuilder
          quoteId={openQuote}
          onClose={() => {
            setOpenQuote(null);
            load().then(notifyCountsChanged);
          }}
        />
      )}

      {reviewing && (
        <QuoteBuilder quoteId={reviewing.quote_id} pendingChanges={reviewing} onClose={finishReview} />
      )}

      <Modal
        isOpen={Boolean(losing)}
        title="Why didn't it book?"
        onClose={() => setLosing(null)}
        maxWidthClass="max-w-md"
      >
        <div className="space-y-2">
          <p className="text-sm text-birdie-charcoal/70 pb-1">
            Marks <strong>{losing?.quote_number}</strong> lost, removes its calendar hold, and turns off
            its payment link.
          </p>
          {LOST_REASONS.map((r) => (
            <button
              key={r}
              disabled={busyId !== null}
              onClick={() => lose(losing, r)}
              className="w-full text-left px-4 py-3 rounded-xl border border-birdie-green/15 bg-white hover:bg-birdie-cream text-sm font-medium text-birdie-charcoal"
            >
              {r}
            </button>
          ))}
        </div>
      </Modal>
    </AppShell>
  );
}

const DOTS = {
  green: "bg-birdie-green",
  mustard: "bg-birdie-mustard",
  blue: "bg-blue-500",
  red: "bg-red-500",
  plain: "bg-birdie-warmgray",
};

function Section({ title, tone, items, children }) {
  if (!items.length) return null;
  return (
    <section>
      <div className="flex items-center gap-2 mb-2 px-0.5">
        <span className={`h-2 w-2 rounded-full ${DOTS[tone]}`} />
        <h2 className="font-accent text-base text-birdie-charcoal">{title}</h2>
        <span className="text-xs text-birdie-charcoal/45">{items.length}</span>
      </div>
      <div className="space-y-2">{items.map(children)}</div>
    </section>
  );
}

function QuoteRow({ q, meta, busy, onResend, onCopy, onLost, onOpen, onEmail, emailed }) {
  const seen = viewSummary(q);
  return (
    <Row
      title={q.customer_name}
      customerId={q.customer_id}
      sub={
        <>
          {q.quote_number} · {formatUSD(q.total_cents)}
          {q.event_date ? ` · ${formatEasternDate(q.event_date)}` : ""}
          {seen && (
            <span className={`block mt-0.5 ${q.view_count ? "text-birdie-green font-semibold" : "text-amber-700"}`}>
              {q.view_count ? "👀 " : ""}
              {seen}
              {!q.view_count && daysSince(q.sent_at) >= 2 && " — check their email address?"}
            </span>
          )}
        </>
      }
      meta={meta}
      emailed={emailed}
    >
      {onEmail && <Act onClick={onEmail}>Email</Act>}
      {q.customer_phone && <LinkBtn href={smsHref(q.customer_phone, quoteNudge(q))}>Text</LinkBtn>}
      {telHref(q.customer_phone) && <LinkBtn href={telHref(q.customer_phone)}>Call</LinkBtn>}
      <Act onClick={onResend} disabled={busy}>
        {busy ? "…" : "Re-send"}
      </Act>
      <Act onClick={onCopy}>Copy link</Act>
      <Act onClick={onLost} disabled={busy}>
        Lost
      </Act>
      <Act onClick={onOpen}>Open</Act>
    </Row>
  );
}

function Row({ title, sub, meta, children, customerId, emailed }) {
  return (
    <div className="bg-white border border-birdie-green/15 rounded-2xl p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {customerId ? (
            <Link to={`/admin/customers/${customerId}`} className="block font-semibold text-birdie-charcoal truncate hover:underline">
              {title}
            </Link>
          ) : (
            <div className="font-semibold text-birdie-charcoal truncate">{title}</div>
          )}
          {sub && <div className="text-xs text-birdie-charcoal/60 mt-0.5">{sub}</div>}
          {emailed && <div className="text-[11px] text-birdie-green mt-0.5">✉️ Emailed {timeAgo(emailed)}</div>}
        </div>
        <div className="text-[11px] text-birdie-charcoal/55 text-right shrink-0">{meta}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-birdie-green/10">{children}</div>
    </div>
  );
}

function LinkBtn({ href, children }) {
  return (
    <a
      href={href}
      className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-birdie-green/20 text-birdie-green hover:bg-birdie-cream"
    >
      {children}
    </a>
  );
}

function Act({ children, onClick, disabled, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-40 whitespace-nowrap ${
        primary
          ? "bg-birdie-green text-white hover:bg-birdie-green-dark"
          : "border border-birdie-green/20 text-birdie-charcoal/75 hover:bg-birdie-cream"
      }`}
    >
      {children}
    </button>
  );
}
